import { memo } from 'react';
import { useAppStore } from '../state/store';
import { SnapIndicator } from '../tools/snapping';
import { TransformPreview } from '../tools/edit';
import { AnalysisOverlay } from './AnalysisOverlayLayer';
import { PolarTrackOverlay } from './PolarTrackOverlay';
import { IsometricGridOverlay } from './IsometricGridOverlay';
import { ApertureRing } from './ApertureRing';
import { ToolGhostPreview } from './ToolGhostPreview';
import { UcsIcon2D } from './UcsIcon2D';

/**
 * System Overlays Layer (Phase 2 decomposition)
 *
 * Isolated behind memo() so tool-hook state changes in SceneContent
 * (pointer-move storms during drawing) never re-render the overlays.
 *
 * Composition:
 * - SnapIndicator     -> OSNAP marker (activeSnap from the snap store)
 * - ApertureRing      -> osnap aperture circle at the cursor (Faz 11.2)
 * - TransformPreview  -> gizmo preview for the selected ids
 * - AnalysisOverlay   -> ZEBRA/DRAFT/CURVATURE/DIR/SHOWEDGES/SECTIONPLANE
 *                        display modes from useViewStore (command parity I/J)
 * - PolarTrackOverlay -> POLAR/ISODRAFT/SMARTTRACK input guides
 * - IsometricGridOverlay -> iso-axis grid while ISODRAFT is on (Faz 7.1)
 * - ToolGhostPreview  -> MOVE/COPY/ROTATE rubber-band ghosts, DRAGMODE-gated
 *                        (Faz 11.8; the only toolPreview subscriber here)
 * - UcsIcon2D         -> origin UCS icon, screen-fixed size (Faz 11.10)
 *
 * NOTE: SelectionBoxOverlay is plain DOM (absolute-positioned div) and must
 * live OUTSIDE the R3F Canvas tree — see Viewport.tsx. The three reconciler
 * crashes with "Div is not part of the THREE namespace" otherwise. The grip
 * drag readout balloon (Faz 11.6) is DOM too — it mounts in the Viewport
 * overlay slot (GripReadoutGhost).
 */
export const SystemOverlayLayer = memo(function SystemOverlayLayer() {
    const selectedIds = useAppStore((state) => state.selectedIds);

    return (
        <>
            <IsometricGridOverlay />
            <SnapIndicator />
            <ApertureRing />
            <TransformPreview selectedIds={selectedIds} />
            <AnalysisOverlay />
            <PolarTrackOverlay />
            <ToolGhostPreview />
            <UcsIcon2D />
        </>
    );
});
